<template lang="pug">
v-flex#source-editor(sm12 md5)
  .overline.mb-2 Spreadsheet

  //- We have a source to work with
  template(v-if="componentSheet")
    v-sheet.mb-3(outlined rounded)
      .d-flex.align-center.px-3.py-2
        spreadsheet-icon(:spreadsheetId="componentSheet.id")
        span.body-2.font-weight-medium.ml-1 {{ componentSheet.name }}
        v-spacer
        refresh-source-button(:spreadsheet="componentSheet")
        v-btn(icon small title="Select a different Spreadsheet" @click="unlinkComponentSheet(component)")
          v-icon(small) close

      template(v-if="componentWorksheet")
        v-divider
        .d-flex.align-center.px-3.py-2
          v-icon(small color="grey") mdi-table
          span.body-2.ml-1 {{ componentWorksheet.title }}
          v-spacer
          v-btn(icon small title="Select a different Worksheet" @click="unlinkComponentWorksheet(component)")
            v-icon(small) close

    template(v-if="componentWorksheet")
      //- Row Selection
      .d-flex.align-center.mb-2
        span.caption Row Selection
        v-switch.ml-2.row-select-toggle(
          dense
          hide-details
          :input-value="showRowSelection"
          @change="val => val ? enableRowSelect() : disableRowSelect()"
        )

      .d-flex.mb-2(v-if="worksheetId && showRowSelection")
        v-text-field.mr-2(dense outlined label="First Row" v-model="firstRow" type="number" min="2" :max="getRowCount(component)+1" hide-details)
        v-text-field(dense outlined label="Last Row" v-model="lastRow" type="number" min="2" :max="getRowCount(component)+1" hide-details)

      //- Quantity Property
      .mb-3
        v-tooltip(bottom)
          template(v-slot:activator="{ on }")
            v-select(dense outlined v-on="on" v-model="quantityProperty" label="Quantity Property" :items="worksheetPropertyNamesWithNull" hide-details)
          | A quantity property duplicates an item any number of times.

      //- Available Properties
      .overline.mb-1 Available Properties
      .properties-chips.mb-3
        v-chip.mr-1.mb-1(
          v-for="property in worksheetPropertyNames(spreadsheetId, worksheetId)"
          :key="property"
          small label outlined
        ) {{ property }}

      magic-property-alignment-panel(:magic-properties="worksheetMagicProperties(spreadsheetId, worksheetId)" :template="componentTemplate")

    //- Worksheet Select
    template(v-else)
      v-btn.mb-2(small @click="createWorksheetDialog = true") Create New Worksheet
      v-select(dense outlined label="Select Worksheet" v-model="worksheetId" :items="worksheetOptions" item-value="id" item-text="title" :error="!worksheetId")
      v-dialog(v-model="createWorksheetDialog" max-width="500")
        v-card
          v-card-text
            v-form(ref="worksheetForm")
              p Add a new worksheet to the Google Sheet "{{ componentSheet.name }}"
              v-text-field(label="Worksheet Name" v-model="worksheetTitle" :rules="[rules.required, rules.noDupes]")
              .d-flex.justify-end.mt-3
                v-btn(@click="createWorksheetDialog = false") Cancel
                v-btn.ml-2(color="primary" @click="createAndLinkWorksheet") Create Worksheet

  //- We need to set a source
  template(v-else)
    p.caption.grey--text.mb-2 This component needs a Spreadsheet to render items from.
    v-btn.mb-2(v-if="gameHasSheet" small @click="useGameSheet") Use Game Spreadsheet
    v-autocomplete(dense outlined label="Select Spreadsheet" v-model="spreadsheetId" :items="allSpreadsheets" item-value="id" item-text="name")
</template>

<script>
  import { includes, map, max, min, pick } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import { computedVModelUpdateAll } from '../util/component_helper'
  import { openSheetPicker } from '../../services/google/picker'
  import MagicPropertyAlignmentPanel from './MagicPropertyAlignmentPanel.vue'
  import SpreadsheetIcon from '../icons/SpreadsheetIcon.vue'
  import RefreshSourceButton from './RefreshSourceButton.vue'

  export default {
    props: ["component"],

    components: {
      SpreadsheetIcon,
      MagicPropertyAlignmentPanel,
      RefreshSourceButton,
    },

    updated() {
      this.detectRowSelection()
      this.validateWorksheetForm()
    },

    data() {
      return {
        createSheetDialog: false,
        createWorksheetDialog: false,
        showRowSelection: false,
        worksheetTitle: this.component.title,
        rules: {
          required: value => !!value || 'Required.',
          noDupes: value => !includes(this.worksheetTitles(this.componentSheet.id), value) || 'No duplicate names.'
        }
      }
    },

    computed: {
      ...mapGetters([
        "workingDirectoryId",
        "worksheetTitles",
        "worksheetPropertyNames",
        "worksheetMagicProperties",
        "findComponentSheet",
        "findComponentTemplate",
        "findSpreadsheetWorksheet",
        "activeGame",
        "gameFolderOrDefault",
        "getRowCount",
        "allSpreadsheets"
      ]),

      ...computedVModelUpdateAll("component", "patchComponent", [
        "quantityProperty"
      ]),

      spreadsheetId: {
        get() { return this.component.spreadsheetId },
        set(spreadsheetId) { this.linkComponentSheet({ component: this.component, spreadsheetId }) }
      },

      worksheetId: {
        get() { return this.component.worksheetId },
        set(worksheetId) { this.setComponentWorksheet({ component: this.component, worksheetId }) }
      },

      worksheetPropertyNamesWithNull() {
        return [ { text: 'No Quantity Expansion', value: null },
                 ...this.worksheetPropertyNames(this.spreadsheetId, this.worksheetId) ]
      },

      firstRow: {
        // 2 offset so the user can work in terms of spreadsheet row numbers
        get() { return this.component.worksheetFirstRow+2 },

        set(newFirstRow) {
          // 2 offset so the user can work in terms of spreadsheet row numbers
          newFirstRow -= 2
          const patchProps = {
            worksheetFirstRow: newFirstRow,
            worksheetLastRow: max([this.component.worksheetLastRow, newFirstRow])
          }
          this.patchComponent({ ...this.component,  ...patchProps })
        }
      },

      lastRow: {
        // 2 offset so the user can work in terms of spreadsheet row numbers
        get() { return this.component.worksheetLastRow+2 },

        set(newLastRow) {
          // 2 offset so the user can work in terms of spreadsheet row numbers
          newLastRow -= 2
          const patchProps = {
            worksheetLastRow: newLastRow,
            worksheetFirstRow: min([this.component.worksheetFirstRow, newLastRow])
          }
          this.patchComponent({ ...this.component,  ...patchProps })
        }
      },

      worksheetOptions() {
        return map(this.componentSheet.worksheets, (ws) => pick(ws, ["id", "title"]))
      },

      gameHasSheet() { return !!this.activeGame.spreadsheetId },

      componentSheet() { return this.findComponentSheet(this.component) },

      componentWorksheet() {
        return this.findSpreadsheetWorksheet(this.componentSheet.id, this.worksheetId)
      },

      componentTemplate() { return this.findComponentTemplate(this.component) },
    },

    methods: {
      ...mapActions([
        "linkComponentSheet",
        "unlinkComponentSheet",
        "setComponentWorksheet",
        "patchComponent",
        "googleCreateSpreadsheet",
        "createComponentSpreadsheetWorksheet",
      ]),

      validateWorksheetForm() {
        this.$refs.worksheetForm && this.$refs.worksheetForm.validate()
      },

      pickSheetFromDrive() {
        return openSheetPicker(this.workingDirectoryId)
          .then((spreadsheetId) => {
            if(!spreadsheetId) { return Promise.reject(new Error("No sheet picked.")) }

            return this.linkComponentSheet({
              component: this.component,
              spreadsheetId
            })
          })
      },

      useGameSheet() {
        return this.patchComponent({
          id: this.component.id,
          spreadsheetId: this.activeGame.spreadsheetId
        })
      },

      createAndLinkWorksheet() {
        this.createComponentSpreadsheetWorksheet({ component: this.component, worksheetTitle: this.worksheetTitle  })

          .then(() => {
            this.createWorksheetDialog = false
          })
      },

      detectRowSelection() {
        this.showRowSelection =
          this.component.worksheetFirstRow      ||
          this.component.worksheetFirstRow == 0 ||
          this.component.worksheetLastRow       ||
          this.component.worksheetLastRow == 0
      },

      enableRowSelect() {
        this.showRowSelection = true
        this.patchComponent({
          ...this.component,
          worksheetFirstRow: 0,
          worksheetLastRow: this.getRowCount(this.component)-1
        })
      },

      unlinkComponentWorksheet(component) {
        this.setComponentWorksheet({ component, worksheetId: null })
      },

      disableRowSelect() {
        this.showRowSelection = false
        this.patchComponent({
          ...this.component,
          worksheetFirstRow: null,
          worksheetLastRow: null
        })
      }
    }
  }
</script>

<style scoped>
.row-select-toggle {
  margin-top: 0 !important;
  padding-top: 0 !important;
  flex: none;
}
.properties-chips {
  display: flex;
  flex-wrap: wrap;
}
.properties-chips .v-chip {
  font-family: 'Roboto', sans-serif;
}
</style>
